/**
 * CreditNoteDetailPage — GST-compliant printable Credit Note view.
 *
 * Route: /billing/credit-note/:cnId
 *
 * Renders:
 *   - Standard letterhead (PrintHeader)
 *   - CN header block: CN No, Date, source invoice no+date, reason
 *   - Bill-to snapshot (patient, GSTIN)
 *   - Lines table (description, HSN, credited qty, rate, tax split, line total)
 *   - Totals footer + amount-in-words
 *   - Settlement footer (refund / adjustment) with method + reference
 *   - Actions: Print A4, Void CN (role-gated)
 *   - Voided badge if status='voided'
 */
import React, { useEffect, useState, useCallback } from 'react';
import useBranchClinic from '../../hooks/useBranchClinic';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { API, fmtINR, fmtDate, fmtDateTime } from './billingUtils';
import { useAuth } from '../../AuthContext';
import PrintHeader from '../../components/PrintHeader';

const REASON_LABELS = {
  data_error:         'Data-entry correction',
  price_adjustment:   'Price adjustment',
  post_sale_discount: 'Post-sale discount',
  return:             'Return of goods',
  service_deficiency: 'Service deficiency',
  duplicate_billing:  'Duplicate billing',
  other:              'Other',
};

const SETTLEMENT_LABELS = {
  adjustment: 'Applied to outstanding balance (no cash movement)',
  refund:     'Refunded to patient',
};

export default function CreditNoteDetailPage() {
  const { cnId } = useParams();
  const { user } = useAuth();
  const [cn, setCn] = useState(null);
  const clinic = useBranchClinic(cn?.branch_id);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/billing/credit-notes/${cnId}`);
      setCn(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load credit note.');
    } finally { setLoading(false); }
  }, [cnId]);
  useEffect(() => { load(); }, [load]);

  const canVoid = cn
    && cn.status !== 'voided'
    && ['super_admin', 'founder', 'clinic_owner', 'accounts'].includes(user?.role);

  const voidCn = async () => {
    const reason = window.prompt('Reason for voiding this credit note (min 5 chars):');
    if (!reason || reason.trim().length < 5) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/billing/credit-notes/${cnId}/void`, {
        reason: reason.trim(),
      });
      setCn(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to void credit note.');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading credit note…</div>;
  if (err && !cn) return <div className="p-6 text-sm text-rose-600" data-testid="cn-page-error">{err}</div>;
  if (!cn) return null;

  return (
    <div className="p-4 space-y-3" data-testid="credit-note-detail">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-2 flex items-center gap-2 flex-wrap print:hidden">
        <Link
          to={`/billing/invoice/${cn.invoice_id}`}
          className="text-xs text-slate-600 hover:text-emerald-700">← Source invoice</Link>
        <div className="flex-1" />
        <button onClick={() => window.print()} data-testid="cn-print"
          className="px-3 py-1 text-xs bg-slate-800 hover:bg-slate-900 text-white font-semibold rounded">
          Print A4
        </button>
        {canVoid && (
          <button onClick={voidCn} disabled={busy} data-testid="cn-void"
            className="px-3 py-1 text-xs border border-rose-300 text-rose-600 hover:bg-rose-50 font-semibold rounded">
            {busy ? 'Voiding…' : 'Void CN'}
          </button>
        )}
      </div>

      {err && (
        <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700 print:hidden"
             data-testid="cn-inline-error">{err}</div>
      )}

      {/* A4 sheet */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm max-w-[820px] mx-auto print:shadow-none print:border-0 print:rounded-none print:max-w-none">
        <PrintHeader
          clinic={clinic}
          docTitle="Credit Note"
          docNo={cn.cn_no}
          docDate={fmtDate(cn.issued_at)}
          docSubtitle={`Against invoice ${cn.invoice_no} · ${fmtDate(cn.invoice_date)}`}
          statusPill={
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
              cn.status === 'voided'
                ? 'bg-slate-200 text-slate-500'
                : 'bg-rose-100 text-rose-700'
            }`} data-testid="cn-status-pill">
              {cn.status === 'voided' ? 'VOIDED' : 'ISSUED'}
            </span>
          }
        />

        {/* Reason banner */}
        <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-[12px]">
          <div className="font-semibold text-rose-700">
            Reason: {REASON_LABELS[cn.reason_code] || cn.reason_code}
          </div>
          <div className="text-slate-700 mt-0.5">{cn.reason_text}</div>
        </div>

        {/* Bill-to */}
        <div className="grid grid-cols-2 gap-4 text-[12px] mt-4">
          <div>
            <div className="text-slate-500 font-semibold uppercase tracking-wide text-[10px] mb-1">Credit To</div>
            <div className="font-semibold text-slate-800">{cn.patient_name}</div>
            {cn.patient_mobile && <div className="text-slate-600">{cn.patient_mobile}</div>}
            {cn.patient_address && <div className="text-slate-600">{cn.patient_address}</div>}
            {cn.patient_gstin && <div className="text-slate-600">GSTIN: {cn.patient_gstin}</div>}
          </div>
          <div className="text-right">
            <div className="text-slate-500 font-semibold uppercase tracking-wide text-[10px] mb-1">Issued</div>
            <div className="text-slate-700">{fmtDateTime(cn.issued_at)}</div>
            {cn.voided_at && (
              <>
                <div className="text-slate-500 font-semibold uppercase tracking-wide text-[10px] mt-2 mb-1">Voided</div>
                <div className="text-slate-700">{fmtDateTime(cn.voided_at)}</div>
                {cn.void_reason && <div className="text-[11px] text-slate-500 mt-1">{cn.void_reason}</div>}
              </>
            )}
          </div>
        </div>

        {/* Lines */}
        <div className="mt-4 border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-semibold text-slate-600">Description</th>
                <th className="px-2 py-1.5 font-semibold text-slate-600">HSN/SAC</th>
                <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Qty</th>
                <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Rate</th>
                <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Taxable</th>
                <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">GST</th>
                <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {cn.lines.map((ln) => (
                <tr key={ln.line_id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 text-slate-700">
                    {ln.description}
                    {ln.serial_numbers && ln.serial_numbers.length > 0 && (
                      <div className="text-[10px] text-slate-500">S/N: {ln.serial_numbers.join(', ')}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 tabular-nums">{ln.hsn_sac || '—'}</td>
                  <td className="px-2 py-1.5 text-right text-slate-600 tabular-nums">
                    {ln.quantity_credited} / {ln.original_quantity}
                  </td>
                  <td className="px-2 py-1.5 text-right text-slate-600 tabular-nums">{fmtINR(ln.unit_price)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums">{fmtINR(ln.taxable_value)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums">
                    {fmtINR(ln.cgst_amount + ln.sgst_amount + ln.igst_amount)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800 tabular-nums">
                    {fmtINR(ln.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td className="px-2 py-1.5 font-semibold text-slate-700" colSpan={4}>Sub-total</td>
                <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums">{fmtINR(cn.subtotal)}</td>
                <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums">{fmtINR(cn.tax_total)}</td>
                <td className="px-2 py-1.5 text-right font-bold text-slate-800 tabular-nums">{fmtINR(cn.grand_total)}</td>
              </tr>
              {cn.round_off !== 0 && (
                <tr>
                  <td className="px-2 py-1.5 text-slate-600" colSpan={6}>Round-off</td>
                  <td className="px-2 py-1.5 text-right text-slate-600 tabular-nums">{fmtINR(cn.round_off)}</td>
                </tr>
              )}
              <tr>
                <td className="px-2 py-1.5 text-sm font-bold text-slate-800" colSpan={6}>Amount Credited</td>
                <td className="px-2 py-1.5 text-right text-sm font-bold text-rose-600 tabular-nums"
                    data-testid="cn-detail-total">
                  {fmtINR(cn.rounded_total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Settlement footer */}
        <div className="mt-4 grid grid-cols-2 gap-4 text-[11px]">
          <div className="border border-slate-200 rounded p-2">
            <div className="text-slate-500 font-semibold uppercase tracking-wide text-[10px] mb-1">
              Settlement
            </div>
            <div className="font-semibold text-slate-800">
              {SETTLEMENT_LABELS[cn.settlement_mode] || cn.settlement_mode}
            </div>
            {cn.settlement_mode === 'refund' && (
              <div className="text-slate-600 mt-0.5">
                Method: <span className="font-semibold">{(cn.refund_method || '').toUpperCase()}</span>
                {cn.refund_reference && <> · Ref: <span className="font-mono">{cn.refund_reference}</span></>}
              </div>
            )}
            {cn.settlement_mode === 'adjustment' && (
              <div className="text-slate-600 mt-0.5">
                Applied against invoice due · {fmtINR(cn.adjusted_against_due)}
              </div>
            )}
          </div>
          <div className="border border-slate-200 rounded p-2">
            <div className="text-slate-500 font-semibold uppercase tracking-wide text-[10px] mb-1">
              Legal note
            </div>
            <div className="text-slate-700 leading-snug">
              This credit note is issued under CGST Rule 53/54 and reduces
              the supplier's output GST liability by the tax amount shown
              above. Report against original tax invoice{' '}
              <span className="font-mono">{cn.invoice_no}</span> in GSTR-1 Table 9B.
            </div>
          </div>
        </div>

        {cn.notes && (
          <div className="mt-3 text-[11px] text-slate-600">
            <span className="font-semibold">Notes: </span>{cn.notes}
          </div>
        )}
      </div>
    </div>
  );
}
